import { paragraphs, splitSentences, stripMarkdown } from "./markdown.ts";

// 年度替わりの見直し（税理士）・法改正の見直し（弁護士）: what in an article
// may change with the tax year or a change in the law.
// Rules only — they point the supervisor at articles to look over; the AI
// review (with official sources) comes after.

export type YearSensitiveKind = "amount" | "relief" | "deadline" | "year" | "reform";

/** What to look for, per kind: a label for the screen and a pattern over sentences. Each profession has its own. */
export type SensitivityRules = Record<YearSensitiveKind, { label: string; pattern: RegExp }>;

/** 税理士: amounts and rates, reliefs, deadlines, the tax year, reforms. */
export const TAX_SENSITIVITY: SensitivityRules = {
  amount: { label: "金額・税率", pattern: /\d[\d,，.]*\s?(?:万円|億円|円|%|％)|税率/ },
  relief: { label: "控除・特例・非課税枠", pattern: /控除|特例|非課税枠|基礎控除|軽減/ },
  deadline: { label: "期限・期間", pattern: /期限|期間内|(?:\d+|[一二三四五六七八九十]+)(?:か月|ヶ月|カ月)以内|月\d+日まで/ },
  year: { label: "年度の書き方", pattern: /令和\d+年度?|\d{4}年度|年度版/ },
  reform: { label: "改正・予定の記述", pattern: /改正|予定|見込み|延長|廃止|創設/ },
};

/** Labels of the tax rules (kept for screens that show the kinds without an office). */
export const YEAR_SENSITIVE_LABELS: Record<YearSensitiveKind, string> = Object.fromEntries(Object.entries(TAX_SENSITIVITY).map(([k, v]) => [k, v.label])) as Record<YearSensitiveKind, string>;

export interface YearSensitivity {
  kind: YearSensitiveKind;
  label: string;
  count: number;
  /** The first sentence that has it, to show why. */
  example: string;
}

/** The kinds of year-dependent content in an article body, most important first (改正 leads). */
export function yearSensitivity(markdown: string, rules: SensitivityRules = TAX_SENSITIVITY): YearSensitivity[] {
  const sentences = [...paragraphs(markdown), ...markdown.split("\n").filter((l) => /^\s*([-*+]|\d+\.|\|)/.test(l))].flatMap((p) => splitSentences(stripMarkdown(p)));
  const order: YearSensitiveKind[] = ["reform", "amount", "relief", "deadline", "year"];
  return order
    .map((kind) => {
      const hits = sentences.filter((s) => rules[kind].pattern.test(s));
      return { kind, label: rules[kind].label, count: hits.length, example: hits[0] ?? "" };
    })
    .filter((x) => x.count > 0);
}
